import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useDatabase, type Truck } from '@/contexts/DatabaseContext';
import DashboardLayout from '@/components/feature/DashboardLayout';

const ITEMS_PER_PAGE = 10;

export default function ManageTrucks() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { trucks, drivers, addTruck, deleteTruck } = useDatabase();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const [newTruck, setNewTruck] = useState({
    doorNumber: '',
    licensePlate: '',
    generalStatus: 'Bon' as 'Bon' | 'En panne' | 'Réparé',
    reportedProblem: '',
    breakdownDate: '',
    repairsDone: '',
    comments: '',
    selectedDriverId: '',
  });

  const sortedTrucks = useMemo(() => {
    return [...trucks].sort((a, b) => {
      const numA = parseInt(a.doorNumber.replace('SILY-', ''), 10);
      const numB = parseInt(b.doorNumber.replace('SILY-', ''), 10);
      return numA - numB;
    });
  }, [trucks]);

  const filteredTrucks = useMemo(() => {
    if (!searchQuery) return sortedTrucks;
    const q = searchQuery.toUpperCase();
    return sortedTrucks.filter(
      (t) =>
        t.doorNumber.toUpperCase().includes(q) ||
        t.licensePlate.toUpperCase().includes(q) ||
        t.driverName.toUpperCase().includes(q)
    );
  }, [sortedTrucks, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredTrucks.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);

  const paginatedTrucks = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filteredTrucks.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTrucks, safePage]);

  function computeRepairStatus(general: string): 'OK' | 'En attente' | 'Terminé' {
    switch (general) {
      case 'Bon':
        return 'OK';
      case 'En panne':
        return 'En attente';
      case 'Réparé':
        return 'Terminé';
      default:
        return 'OK';
    }
  }

  const handleAddTruck = () => {
    if (!newTruck.licensePlate) return;

    const selectedDriver = drivers.find((d) => d.id === newTruck.selectedDriverId);
    const nextNumber =
      trucks.length > 0
        ? Math.max(...trucks.map((t) => parseInt(t.doorNumber.replace('SILY-', ''), 10) || 0)) + 1
        : 1;

    const truck: Truck = {
      id: String(Date.now()),
      doorNumber:
        newTruck.doorNumber || `SILY-${String(nextNumber).padStart(2, '0')}`,
      licensePlate: newTruck.licensePlate,
      generalStatus: newTruck.generalStatus,
      reportedProblem: newTruck.reportedProblem,
      breakdownDate: newTruck.breakdownDate,
      repairsDone: newTruck.repairsDone,
      repairStatus: computeRepairStatus(newTruck.generalStatus),
      comments: newTruck.comments,
      driverName: selectedDriver?.name || '',
      driverContact: selectedDriver?.contact || '',
    };

    addTruck(truck);
    setShowAddForm(false);
    setNewTruck({
      doorNumber: '',
      licensePlate: '',
      generalStatus: 'Bon',
      reportedProblem: '',
      breakdownDate: '',
      repairsDone: '',
      comments: '',
      selectedDriverId: '',
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'OK':
        return 'bg-primary-100 text-primary-800';
      case 'En attente':
        return 'bg-accent-100 text-accent-800';
      case 'Terminé':
        return 'bg-primary-100 text-primary-800';
      case 'Bon':
        return 'bg-primary-100 text-primary-800';
      case 'En panne':
        return 'bg-accent-100 text-accent-800';
      case 'Réparé':
        return 'bg-primary-100 text-primary-800';
      default:
        return 'bg-background-100 text-foreground-600';
    }
  };

  const getRepairLabel = (status: string) => {
    switch (status) {
      case 'OK':
        return t('ok');
      case 'En attente':
        return t('waiting');
      case 'Terminé':
        return t('done');
      default:
        return status;
    }
  };

  const getGeneralLabel = (status: string) => {
    switch (status) {
      case 'Bon':
        return t('good');
      case 'En panne':
        return t('broken');
      case 'Réparé':
        return t('repaired');
      default:
        return status;
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              {t('manageTrucks')}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              {trucks.length} {t('trucks').toLowerCase()}
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-add-line" />
            {t('addNewTruck')}
          </button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-6 mb-6">
            <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-4">
              {t('addNewTruck')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('doorNumber')}</label>
                <input
                  type="text"
                  value={newTruck.doorNumber}
                  onChange={(e) => setNewTruck((prev) => ({ ...prev, doorNumber: e.target.value }))}
                  placeholder="SILY-61"
                  className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('licensePlate')}</label>
                <input
                  type="text"
                  value={newTruck.licensePlate}
                  onChange={(e) => setNewTruck((prev) => ({ ...prev, licensePlate: e.target.value }))}
                  placeholder="BB 0000-02-BB 0000-02"
                  className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('driver')}</label>
                <select
                  value={newTruck.selectedDriverId}
                  onChange={(e) => setNewTruck((prev) => ({ ...prev, selectedDriverId: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <option value="">{t('selectDriver')}</option>
                  {drivers.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('generalStatus')}</label>
                <select
                  value={newTruck.generalStatus}
                  onChange={(e) =>
                    setNewTruck((prev) => ({
                      ...prev,
                      generalStatus: e.target.value as 'Bon' | 'En panne' | 'Réparé',
                    }))
                  }
                  className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <option value="Bon">{t('good')}</option>
                  <option value="En panne">{t('broken')}</option>
                  <option value="Réparé">{t('repaired')}</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('reportedProblem')}</label>
                <input
                  type="text"
                  value={newTruck.reportedProblem}
                  onChange={(e) => setNewTruck((prev) => ({ ...prev, reportedProblem: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleAddTruck}
                disabled={!newTruck.licensePlate}
                className="px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line mr-1.5" />
                {t('save')}
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-5 mb-4">
          <div className="relative max-w-md">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-foreground-400" />
            </div>
            <input
              type="text"
              placeholder={`${t('search')}...`}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-background-100 border-b border-background-200">
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    {t('doorNumber')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    {t('licensePlate')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden lg:table-cell">
                    {t('driver')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden md:table-cell">
                    {t('repairStatus')}
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider text-right">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {paginatedTrucks.map((truck) => (
                  <tr
                    key={truck.id}
                    className="hover:bg-background-100/50 transition-colors cursor-pointer"
                    onDoubleClick={() => navigate(`/trucks/${truck.id}`)}
                  >
                    <td className="px-4 py-3 text-sm font-semibold text-foreground-900">
                      {truck.doorNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700">
                      {truck.licensePlate}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(truck.generalStatus)}`}
                      >
                        {getGeneralLabel(truck.generalStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 hidden lg:table-cell">
                      {truck.driverName || '-'}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(truck.repairStatus)}`}
                      >
                        {getRepairLabel(truck.repairStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {deleteConfirm === truck.id ? (
                        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => { deleteTruck(truck.id); setDeleteConfirm(null); }}
                            className="px-3 py-1.5 bg-accent-500 text-background-50 rounded-lg text-xs font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
                          >
                            {t('delete')}
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="px-3 py-1.5 bg-background-100 text-foreground-600 rounded-lg text-xs font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
                          >
                            {t('cancel')}
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <Link
                            to={`/trucks/${truck.id}?edit=true`}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-background-200 text-foreground-500 hover:text-foreground-800 transition-colors"
                          >
                            <i className="ri-edit-line text-base" />
                          </Link>
                          <button
                            onClick={() => setDeleteConfirm(truck.id)}
                            title={t('deleteTruck')}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-accent-50 text-foreground-400 hover:text-accent-600 transition-colors"
                          >
                            <i className="ri-delete-bin-line text-base" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredTrucks.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-sm text-foreground-500">{t('noResults')}</p>
            </div>
          )}

          {filteredTrucks.length > 0 && (
            <div className="px-4 py-3 bg-background-100 border-t border-background-200 flex items-center justify-between">
              <span className="text-xs text-foreground-500">
                {t('page')} {safePage} {t('of')} {totalPages}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}